import { ensureDirectory, findFile, hasDirectory, listDirectories, listFiles, putFile, readFileBlob, removeDirectory, roleForPath, trashFile } from '../common/library/database';
import type { CraftId } from '../common/library/types';

function fail(name: string, message: string): never { throw new DOMException(message, name); }
function childPath(parent: string, name: string): string {
  if (!name || name === '.' || name === '..' || /[\\/\0]/.test(name)) fail('TypeError', 'Недопустимое имя файла');
  return `${parent}/${name}`;
}

export function installIndexedFileSystem(appId: CraftId, changed: (name: string, id: string) => void) {
  const constructors = {
    directory: globalThis.FileSystemDirectoryHandle?.prototype ?? {},
    file: globalThis.FileSystemFileHandle?.prototype ?? {},
    writable: globalThis.FileSystemWritableFileStream?.prototype ?? WritableStream.prototype,
  };
  const handles = new WeakMap<object, string>();
  function identity(handle: object, path: string) {
    handles.set(handle, path);
    Object.defineProperties(handle, {
      name: { value: path.split('/').pop() ?? '' },
      isSameEntry: { value: async (other: object) => handles.get(other) === path },
      queryPermission: { value: async () => 'granted' }, requestPermission: { value: async () => 'granted' },
    });
    return handle;
  }
  function file(path: string): FileSystemFileHandle {
    const handle = identity(Object.create(constructors.file), path);
    Object.defineProperties(handle, {
      kind: { value: 'file' },
      getFile: { value: async () => {
        const record = await findFile(appId, path);
        if (!record) fail('NotFoundError', 'Файл не найден');
        return readFileBlob(record.id);
      } },
      createWritable: { value: async (options: FileSystemCreateWritableOptions = {}) => {
        const record = await findFile(appId, path);
        let contents = options.keepExistingData && record ? await readFileBlob(record.id) : new Blob([]);
        let position = 0;
        let aborted = false;
        const sink: UnderlyingSink<FileSystemWriteChunkType> = {
          async write(chunk) {
            if (typeof chunk === 'object' && !(chunk instanceof Blob) && !(chunk instanceof ArrayBuffer) && !ArrayBuffer.isView(chunk) && 'type' in chunk) {
              if (chunk.type === 'seek') { position = chunk.position ?? 0; return; }
              if (chunk.type === 'truncate') { const size = chunk.size ?? 0; contents = resize(contents, size); position = Math.min(position, size); return; }
              if (chunk.position != null) position = chunk.position;
              chunk = chunk.data ?? new Blob([]);
            }
            const piece = chunk instanceof Blob ? chunk : new Blob([chunk as BlobPart]);
            if (position > contents.size) contents = resize(contents, position);
            contents = new Blob([contents.slice(0, position), piece, contents.slice(position + piece.size)]);
            position += piece.size;
          },
          async close() {
            if (aborted) return;
            const stored = await putFile(contents, path.split('/').pop() ?? 'Файл', { appId, path, role: roleForPath(path) });
            if (stored.role !== 'internal') changed(stored.name, stored.id);
          },
          abort() { aborted = true; },
        };
        const stream = new WritableStream<FileSystemWriteChunkType>(sink);
        Object.setPrototypeOf(stream, constructors.writable);
        const write = async (chunk: FileSystemWriteChunkType) => { const writer = stream.getWriter(); try { await writer.write(chunk); } finally { writer.releaseLock(); } };
        Object.defineProperties(stream, {
          write: { value: write },
          seek: { value: (position: number) => write({ type: 'seek', position }) },
          truncate: { value: (size: number) => write({ type: 'truncate', size }) },
          close: { value: async () => { const writer = stream.getWriter(); try { await writer.close(); } finally { writer.releaseLock(); } } },
        });
        return stream;
      } },
    });
    return handle as FileSystemFileHandle;
  }
  function directory(path: string): FileSystemDirectoryHandle {
    const handle = identity(Object.create(constructors.directory), path);
    async function* entries(): AsyncGenerator<[string, FileSystemHandle]> {
      const prefix = `${path}/`;
      for (const candidate of await listDirectories(appId)) {
        if (!candidate.startsWith(prefix)) continue;
        const rest = candidate.slice(prefix.length);
        if (rest && !rest.includes('/')) yield [rest, directory(candidate)];
      }
      for (const record of await listFiles(appId, true)) {
        if (!record.path.startsWith(prefix)) continue;
        const rest = record.path.slice(prefix.length);
        if (rest && !rest.includes('/')) yield [rest, file(record.path)];
      }
    }
    Object.defineProperties(handle, {
      kind: { value: 'directory' },
      getDirectoryHandle: { value: async (name: string, options: FileSystemGetDirectoryOptions = {}) => {
        const child = childPath(path, name);
        if (!await hasDirectory(appId, child)) {
          if (!options.create) fail('NotFoundError', 'Папка не найдена');
          if (await findFile(appId, child)) fail('TypeMismatchError', 'Вместо папки найден файл');
          await ensureDirectory(appId, child);
        }
        return directory(child);
      } },
      getFileHandle: { value: async (name: string, options: FileSystemGetFileOptions = {}) => {
        const child = childPath(path, name);
        if (!await findFile(appId, child)) {
          if (!options.create) fail('NotFoundError', 'Файл не найден');
          if (await hasDirectory(appId, child)) fail('TypeMismatchError', 'Вместо файла найдена папка');
          await putFile(new Blob([]), name, { appId, path: child, role: 'internal' });
        }
        return file(child);
      } },
      entries: { value: entries }, [Symbol.asyncIterator]: { value: entries },
      values: { value: async function* () { for await (const [, value] of entries()) yield value; } },
      keys: { value: async function* () { for await (const [key] of entries()) yield key; } },
      resolve: { value: async (other: object) => { const child = handles.get(other); return child === path ? [] : child?.startsWith(`${path}/`) ? child.slice(path.length + 1).split('/') : null; } },
      removeEntry: { value: async (name: string, options: FileSystemRemoveOptions = {}) => {
        const child = childPath(path, name);
        const record = await findFile(appId, child);
        if (record) { await trashFile(record.id); return; }
        const directories = (await listDirectories(appId)).filter((entry) => entry === child || entry.startsWith(`${child}/`));
        const files = (await listFiles(appId, true)).filter((entry) => entry.path.startsWith(`${child}/`));
        if (!directories.length) fail('NotFoundError', 'Папка не найдена');
        if (!options.recursive && (directories.length > 1 || files.length)) fail('InvalidModificationError', 'Папка содержит файлы');
        for (const record of files) await trashFile(record.id);
        for (const entry of directories) await removeDirectory(appId, entry);
      } },
    });
    return handle as FileSystemDirectoryHandle;
  }
  // Keep the StorageManager wrapper alive. WebKit may recreate a wrapper after
  // WASM compilation, losing an override installed only on the old instance.
  const manager = navigator.storage;
  Object.defineProperty(manager, 'getDirectory', { configurable: true, value: async () => directory('') });
  Object.defineProperty(navigator, 'storage', { configurable: true, get: () => manager });
}
function resize(blob: Blob, size: number): Blob {
  if (!Number.isSafeInteger(size) || size < 0) fail('TypeError', 'Недопустимый размер файла');
  if (size <= blob.size) return blob.slice(0, size);
  const blocks: BlobPart[] = [blob];
  let remaining = size - blob.size;
  while (remaining) { const amount = Math.min(remaining, 4 * 1024 * 1024); blocks.push(new Uint8Array(amount)); remaining -= amount; }
  return new Blob(blocks);
}
